// frontend/src/features/categories/EditCategoryForm.js
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Form, Button, Spinner, Alert } from 'react-bootstrap';
import { useUpdateCategoryMutation, useUploadCategoryImageMutation } from './categoriesApiSlice'; // NEW: Import useUploadCategoryImageMutation
import useTitle from '../../hooks/useTitle';

const EditCategoryForm = ({ category }) => {
    useTitle(`Nirakar Satguru: Edit ${category?.name || 'Category'}`);

    const navigate = useNavigate();

    const [updateCategory, {
        isLoading: isUpdating, // Renamed to avoid conflict with upload isLoading
        isSuccess: isUpdateSuccess,
        isError: isUpdateError,
        error: updateError
    }] = useUpdateCategoryMutation();

    const [uploadImage, {
        isLoading: isUploading, // Loading state for image upload
        isError: isUploadError,
        error: uploadError
    }] = useUploadCategoryImageMutation(); // NEW: Image upload mutation hook

    const [name, setName] = useState(category.name);
    const [description, setDescription] = useState(category.description);
    const [imageUrl, setImageUrl] = useState(category.imageUrl || ''); // Holds the current URL or new uploaded URL
    const [imageFile, setImageFile] = useState(null); // NEW: Holds the selected image file
    const [imagePreview, setImagePreview] = useState(category.imageUrl || ''); // NEW: For showing image preview
    const [isActive, setIsActive] = useState(category.isActive);
    const [uploadMessage, setUploadMessage] = useState(''); // For showing upload status/errors

    // Effect to update imagePreview when imageFile changes
    useEffect(() => {
        if (imageFile) {
            const reader = new FileReader();
            reader.onloadend = () => {
                setImagePreview(reader.result);
            };
            reader.readAsDataURL(imageFile);
        } else {
            setImagePreview(category.imageUrl || ''); // Reset to existing URL if no new file
        }
    }, [imageFile, category.imageUrl]);

    // Handle successful update
    useEffect(() => {
        if (isUpdateSuccess) {
            navigate('/dashboard/categories');
        }
    }, [isUpdateSuccess, navigate]);

    // Handle file selection
    const handleFileChange = (e) => {
        if (e.target.files[0]) {
            setImageFile(e.target.files[0]);
            setUploadMessage(''); // Clear previous upload messages
        } else {
            setImageFile(null);
            setImagePreview(category.imageUrl || ''); // Reset preview if no file selected
        }
    };

    const canSave = [name, description].every(Boolean) && !isUpdating && !isUploading; // Disable save during update/upload

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!canSave) return;

        let newImageUrl = imageUrl; // Start with the current URL

        // If a new image file is selected, upload it first
        if (imageFile) {
            setUploadMessage('Uploading image...');
            const formData = new FormData();
            formData.append('image', imageFile); // 'image' should match your backend's expected field name

            try {
                const uploadResponse = await uploadImage(formData).unwrap();
                newImageUrl = uploadResponse.imageUrl; // Assuming backend returns { imageUrl: '...' }
                setUploadMessage('Image uploaded successfully.');
            } catch (err) {
                console.error('Failed to upload image:', err);
                setUploadMessage(`Image upload failed: ${err?.data?.message || 'Server error'}`);
                return; // Stop if image upload fails
            }
        }

        // Now, update the category with the new (or existing) image URL
        try {
            await updateCategory({
                id: category.id,
                name,
                description,
                imageUrl: newImageUrl, // Use the newImageUrl
                isActive
            }).unwrap(); // Use unwrap() to handle errors directly
            // Success is handled by useEffect
        } catch (err) {
            console.error('Failed to update category:', err);
            // Error message will be displayed by the Alert component
        }
    };

    if (!category) {
        return <Alert variant="danger">Category data not provided to form.</Alert>;
    }

    return (
        <div className="container mt-4">
            <h2>Edit Category: {category.name}</h2>
            {isUpdateError && <Alert variant="danger">{updateError?.data?.message || 'Failed to update category.'}</Alert>}
            {isUploadError && <Alert variant="danger">{uploadError?.data?.message || 'Failed to upload image.'}</Alert>}
            {uploadMessage && !isUploadError && <Alert variant="info">{uploadMessage}</Alert>}
            {isUpdateSuccess && <Alert variant="success">Category updated successfully!</Alert>}


            <Form onSubmit={handleSubmit}>
                <Form.Group className="mb-3" controlId="name">
                    <Form.Label>Category Name:</Form.Label>
                    <Form.Control
                        type="text"
                        placeholder="Enter category name"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        required
                    />
                </Form.Group>

                <Form.Group className="mb-3" controlId="description">
                    <Form.Label>Description:</Form.Label>
                    <Form.Control
                        as="textarea"
                        rows={3}
                        placeholder="Enter category description"
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        required
                    />
                </Form.Group>

                <Form.Group className="mb-3" controlId="imageFile">
                    <Form.Label>Category Image:</Form.Label>
                    <Form.Control
                        type="file"
                        onChange={handleFileChange}
                        accept="image/*" // Only allow image files
                    />
                    {imagePreview && (
                        <div className="mt-2">
                            <p className="mb-1">Current/New Image Preview:</p>
                            <img src={imagePreview} alt="Image Preview" style={{ maxWidth: '150px', height: 'auto' }} className="rounded" />
                        </div>
                    )}
                    {/* Optional: Display current URL if no new file is chosen */}
                    {!imageFile && imageUrl && (
                        <Form.Text className="text-muted mt-2 d-block">
                            Current Image URL: {imageUrl}
                        </Form.Text>
                    )}
                </Form.Group>

                <Form.Group className="mb-3" controlId="isActive">
                    <Form.Check
                        type="checkbox"
                        label="Is Active"
                        checked={isActive}
                        onChange={(e) => setIsActive(e.target.checked)}
                    />
                </Form.Group>

                <Button variant="primary" type="submit" disabled={!canSave || isUploading}>
                    {(isUpdating || isUploading) ? <Spinner animation="border" size="sm" /> : 'Update Category'}
                </Button>
                <Button variant="secondary" onClick={() => navigate('/dashboard/categories')} className="ms-2">
                    Cancel
                </Button>
            </Form>
        </div>
    );
};

export default EditCategoryForm;